import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { formatDateRangeForMonth } from "@/utils/globalUtils";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Button, DatePicker, Form, Select, Spin } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import WaterReportBody from "./WaterReportBody";
import WaterReportPDF from "./WaterReportPDF";
import { flare } from "@flareapp/js";

const initReportValues = {};

const WaterReport = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [reportValues, setReportValues] = useState(initReportValues);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessList, setBusinessList] = useState([]);

  const [siteLoading, setSiteLoading] = useState(false);
  const [siteList, setSiteList] = useState([]);

  const [deviceList, setDeviceList] = useState([]);
  const [isDeviceLoading, setIsDeviceLoading] = useState(false);

  const getBusiness = async () => {
    setBusinessLoading(true);
    try {
      const data = await api.get(endpoint.business);
      setBusinessList(data?.data || []);
      if (data?.data && data?.data?.length === 1) {
        const bID = data?.data[0]?._id;
        form.setFieldValue("business", bID);
        getSiteLocations(bID);
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("business list", error);
    }
    setBusinessLoading(false);
  };

  const getSiteLocations = async (business) => {
    setSiteLoading(true);
    if (business) {
      try {
        const data = await api.get(`/site-location/my-site`, {
          params: { limit: 100000, populate: "businesses", business },
        });
        setSiteList(data || []);
      } catch {
        setSiteList([]);
      }
    }
    setSiteLoading(false);
  };

  const getDevice = async (site) => {
    if (!site?.length) return setDeviceList([]);
    setIsDeviceLoading(true);
    try {
      setDeviceList([]);
      const { results } = await api.get(endpoint.get_devices, {
        params: {
          site,
          limit: 100000,
          page: 1,
          hasWaterTariff: 1,
          populate: "waterTariff",
        },
      });

      if (results) {
        setDeviceList(results);
      }
    } catch {
      setDeviceList([]);
    } finally {
      setIsDeviceLoading(false);
    }
  };

  const getWaterTariffInfo = async (params) => {
    setIsLoading(true);
    try {
      const result = await api.get(endpoint.get_water_tariff_data || "/device/water-tariff-data", {
        params,
      });
      return result;
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setIsLoading(false);
    }
    return {};
  };

  useEffect(() => {
    document.title = `${appConfig.name} - Water Usage Reports`;
    getBusiness();
  }, []);

  const onFinish = async (values) => {
    const { site, device, month, type, business } = values;
    const selectedSite = siteList.find((s) => s._id === site) || {};
    const selectedDevice = deviceList.find((d) => d._id === device) || {};

    const waterPricing = await getWaterTariffInfo({
      device,
      month: month.format("MMM"),
      year: month.format("YYYY"),
      tax: type,
    });

    const firstBusiness = businessList.find((b) => b._id === business) || businessList[0];

    setReportValues((pre) => ({
      ...pre,
      reportGenerationDate: moment().format("HH:mm:ss | DD/MM/YYYY") || "",
      monthlyCycleSelected: month ? formatDateRangeForMonth(month) : "",
      site: selectedSite?.name || "",
      siteAddress1: selectedSite?.buildingAddress1 || "",
      siteAddress2: selectedSite?.buildingAddress2 || "",
      device: selectedDevice?.name || "",
      installedArea: selectedDevice?.installedArea || "",
      business: selectedSite?.businesses?.name || "",
      type,
      tariffName: selectedDevice?.waterTariff?.name || waterPricing?.tariffName || "",
      totalUsageM3: waterPricing?.totalUsageM3 || 0,
      calculatedUsageCost: waterPricing?.calculatedUsageCost || 0,
      minimumCharge: waterPricing?.minimumCharge || 0,
      waterUsageCharge: waterPricing?.waterUsageCharge || 0,
      serviceTax: waterPricing?.serviceTax || 0,
      totalCharge: waterPricing?.totalCharge || 0,
      firstBusiness,
    }));
  };

  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const isDisabled = !formData?.site || !formData?.device || !formData?.month;

  return (
    <Spin spinning={isLoading}>
      <div className="analysis">
        <div className="card p-3 m-md-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <label className="az-content-label mb-0">Manage Analysis and Reporting</label>
              <span className="d-block py-2">Water Usage Reports</span>
            </div>
          </div>
          <hr className="" />

          <div>
            <Form
              name="water-report-form"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              form={form}
              onValuesChange={(_, values) => {
                setFormData(values);
              }}
              initialValues={{}}
            >
              <Row lg={6} md={3} sm={2} xs={1} className="g-3">
                <Col>
                  <Form.Item name="business" className="w-100">
                    <Select
                      showSearch={true}
                      placeholder="All Business"
                      loading={businessLoading}
                      allowClear
                      disabled={businessList.length === 1}
                      onClear={() => {
                        setDeviceList([]);
                        setSiteList([]);
                      }}
                      onChange={(values) => {
                        getSiteLocations(values);
                        form.setFieldValue("site", undefined);
                        form.setFieldValue("device", undefined);
                        setFormData((pre) => ({ ...pre, site: undefined, device: undefined }));
                      }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        ...businessList.map((item) => ({
                          label: `${item.name} ${item?.id ? "(" + item.id + ")" : ""} `,
                          value: item._id,
                        })),
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="site" className="w-100">
                    <Select
                      showSearch={true}
                      placeholder="All Site Locations"
                      loading={siteLoading}
                      allowClear
                      onClear={() => {
                        setDeviceList([]);
                      }}
                      onChange={(values) => {
                        getDevice(values);
                        form.setFieldValue("device", undefined);
                        setFormData((pre) => ({ ...pre, device: undefined }));
                      }}
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        ...siteList.map((item) => ({
                          label: `${item.name} ${item?.id ? "(" + item.id + ")" : ""} `,
                          value: item._id,
                        })),
                      ]}
                    />
                  </Form.Item>
                </Col>

                <Col>
                  <Form.Item name="device">
                    <Select
                      style={{ width: "100%" }}
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      maxTagCount="responsive"
                      placeholder="Select Water Meter"
                      loading={isDeviceLoading}
                      options={
                        Array.isArray(deviceList)
                          ? deviceList?.map((item) => ({ label: item.name, value: item._id }))
                          : []
                      }
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="month">
                    <DatePicker
                      picker="month"
                      format={"MMM, YYYY"}
                      allowClear
                      style={{ width: "100%" }}
                      disabledDate={disabledDate}
                    />
                  </Form.Item>
                </Col>

                <Col>
                  <Form.Item name="type" className="w-100">
                    <Select
                      showSearch={true}
                      placeholder="Select Type"
                      loading={siteLoading}
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                      options={[
                        { label: "With Service Tax (8%)", value: 8 },
                        { label: "Without Service Tax (0%)", value: 0 },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col>
                  <Button htmlType="submit" type="primary" disabled={isDisabled}>
                    Generate
                  </Button>
                </Col>
              </Row>

              {/* Report Body */}
              <WaterReportBody reportValues={reportValues} />

              <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
                <PDFDownloadLink
                  document={<WaterReportPDF reportValues={reportValues} />}
                  fileName={`Water Report${reportValues?.site ? " - " + reportValues?.site : ""} ${
                    formData?.month?.isValid() ? " - " + formData?.month?.format("MMM YYYY") : ""
                  } - ${moment().format("DD.M.YY")}.pdf`}
                >
                  {({ loading }) => (
                    <Button disabled={isDisabled} type="primary" loading={loading || isLoading}>
                      Download PDF
                    </Button>
                  )}
                </PDFDownloadLink>

                <Button type="primary" className="bg-secondary" onClick={() => navigate("/data-analysis")}>
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default WaterReport;

const disabledDate = (current) => {
  let date = new Date();
  const currentDate = moment(date).format("MMM, YYYY");
  const momentDate = current.format("MMM, YYYY");
  const dayOfCurrentMonth = date.getDate();
  const dayFlag = dayOfCurrentMonth < 7 && currentDate === momentDate;
  const dateCurrent = Number(moment(date).format("x"));
  return (current && current.valueOf() > dateCurrent) || dayFlag;
};
